import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { mulberry32 } from '../core/rng.js';
import { stampHouse } from './props.js';
import { weld } from '../core/merge.js';
import { createChurch } from './church.js';
import { PICK_MATERIAL } from '../core/materials.js';
import { LANDMARKS } from '../config.js';

// ---------------------------------------------------------------------------
// Volendam.
//
// The mills are what you recognise the Zaanse Schans by; the lighthouse will
// do for Marken. Volendam's own signature is its harbour: a horseshoe of quay
// packed with black wooden botters, so what you see from the water is a
// thicket of masts, with the tall narrow houses of the dijk behind them.
//
// So that is what gets built — the basin first, cut out of the island, then
// the quay around it, then the fleet, then the houses looking down on it.
// ---------------------------------------------------------------------------

export function registerVolendamBlocks(palette) {
    return {
        quay: palette.add('volQuay', 0x7c7166, { jitter: 0.06 }),
        bollard: palette.add('volBollard', 0x40382c, { jitter: 0.06 }),
        hull: palette.add('volHull', 0x22262c, { jitter: 0.05 }),
        gunwale: palette.add('volGunwale', 0x8a6a3f, { jitter: 0.06 }),
        mast: palette.add('volMast', 0x6b4f2a, { jitter: 0.06 }),
        sail: palette.add('volSail', 0xa86a48, { jitter: 0.05 }),  // furled, tanned
        wall: palette.add('volWall', 0x2f4636, { jitter: 0.05 }),  // dark green boarding
        wallPale: palette.add('volWallPale', 0xe4dfd0, { jitter: 0.04 }),
        roof: palette.add('volRoof', 0x74392f, { jitter: 0.07 }),
    };
}

/**
 * Cut the harbour basin out of the island and wall it with quay.
 *
 * The basin opens seaward on `mouth` so a boat can actually get in — a
 * harbour you cannot enter is a pond.
 */
function carveHarbour(grid, polder, P, { mouth, radius }) {
    const { size, seaLevel } = polder;
    const mid = size / 2;

    // How far the land reaches on the bearing the harbour opens towards. The
    // basin is centred just inside that, so the circle straddles the coast and
    // the mouth is open water by construction rather than by a channel dug
    // through afterwards — a harbour you cannot enter is a pond.
    let coast = 0;
    for (let r = 0; r < size / 2; r += 0.5) {
        const x = Math.round(mid + Math.sin(mouth) * r);
        const z = Math.round(mid + Math.cos(mouth) * r);
        if (polder.top(x, z) >= seaLevel) coast = r;
    }

    // Most of the circle has to fall inside the land or there is no harbour,
    // only a bay: the arms either side of the mouth are what make it one. Sit
    // it too far out and it does not cut a notch, it eats the whole coast.
    const reach = coast - radius * 0.62;
    const cx = Math.round(mid + Math.sin(mouth) * reach);
    const cz = Math.round(mid + Math.cos(mouth) * reach);
    const bed = seaLevel - 3;

    for (let z = 0; z < size; z++) {
        for (let x = 0; x < size; x++) {
            const d = Math.hypot(x - cx, z - cz);
            if (d > radius) continue;

            const i = z * size + x;
            const was = polder.heights[i];
            if (was <= bed) continue;

            // Clear everything above the new bed, and remember the new height
            // so the water knows how deep it is in here.
            for (let y = bed; y <= was; y++) grid.set(x, y, z, 0);
            polder.heights[i] = bed;
            polder.surface[i] = polder.blocks.mud;

            // The rim becomes quay — but only where the harbour meets land.
            // Walling the seaward arc as well would close the mouth.
            const fromMiddle = Math.hypot(x - mid, z - mid);
            if (d > radius - 1.5 && was > seaLevel && fromMiddle < coast - 0.5) {
                for (let y = bed; y <= seaLevel + 1; y++) grid.set(x, y, z, P.quay);
                polder.heights[i] = seaLevel + 2;
                polder.surface[i] = P.quay;
            }
        }
    }
    return { cx, cz, radius, bed, coast };
}

/** A botter: black hull, one mast, a tanned sail furled along the boom. */
function createBotter(palette, material, P, { voxelSize, rng }) {
    const L = 9;
    const B = 4;
    const MAST = 11;
    const g = new VoxelGrid(L, MAST + 2, B);
    const cz = (B - 1) >> 1;

    for (let x = 0; x < L; x++) {
        const taper = Math.abs(x - (L - 1) / 2) / ((L - 1) / 2);
        const inset = Math.round(taper * 1.4);
        const z0 = inset;
        const z1 = B - 1 - inset;
        if (z0 > z1) continue;
        g.box(x, 0, z0, x, 1, z1, P.hull);
        g.box(x, 2, z0, x, 2, z1, P.gunwale);
    }

    const mx = Math.round(L * 0.45);
    for (let y = 3; y < MAST; y++) g.set(mx, y, cz, P.mast);
    // Sail furled along the boom rather than set: these are moored.
    g.box(mx - 3, 3, cz, mx + 2, 3, cz, P.sail);
    if (rng() < 0.5) g.box(mx, MAST - 4, cz, mx + 2, MAST - 4, cz, P.sail);

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(-(L / 2) * voxelSize, -1.5 * voxelSize, -(B / 2) * voxelSize),
        }),
        material,
    );
    mesh.name = 'botter';
    return mesh;
}

export function buildVolendam(scene, grid, polder, palette, material, P, props, church, { voxelSize, seed, facing }) {
    const rng = mulberry32(seed);
    const { size, seaLevel } = polder;

    // The harbour faces the way boats arrive — towards the Zaanse Schans,
    // which is where they all come from. Passed in rather than guessed, so
    // moving an island turns the harbour to suit rather than leaving it
    // opening onto empty sea.
    const mouth = facing;
    // Big enough for the visitor's boat to get in and turn — she is seven
    // units long — but small against the island, or the harbour swallows it.
    const basin = carveHarbour(grid, polder, P, { mouth, radius: Math.round(size * 0.16) });

    // --- the fleet, moored stern-on to the quay ---------------------------
    const fleet = [];
    const moored = Math.round(7 + rng() * 4);
    for (let i = 0; i < moored; i++) {
        // Along the back of the basin, away from the mouth.
        const a = mouth + Math.PI + (i / (moored - 1) - 0.5) * 2.3;
        const r = basin.radius * (0.62 + rng() * 0.2);
        const gx = basin.cx + Math.sin(a) * r;
        const gz = basin.cz + Math.cos(a) * r;
        if (gx < 1 || gz < 1 || gx > size - 2 || gz > size - 2) continue;

        const boat = createBotter(palette, material, P, { voxelSize, rng });
        const at = polder.toWorld(gx, gz);
        boat.position.set(at.x, (seaLevel - 0.5) * voxelSize, at.z);
        // Bows to the middle of the basin, as a boat lies to its warps.
        boat.rotation.y = a + Math.PI / 2 + (rng() - 0.5) * 0.25;
        boat.rotation.z = (rng() - 0.5) * 0.05;
        boat.updateMatrixWorld(true);
        fleet.push(boat);
    }

    // Moored boats do not move. One mesh for the whole harbour.
    const welded = weld(fleet, material, 'botters');
    if (welded) scene.add(welded);

    // --- bollards along the quay ------------------------------------------
    for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2;
        const gx = Math.round(basin.cx + Math.sin(a) * (basin.radius - 0.5));
        const gz = Math.round(basin.cz + Math.cos(a) * (basin.radius - 0.5));
        if (polder.top(gx, gz) < seaLevel) continue;
        if (Math.hypot(gx - size / 2, gz - size / 2) > basin.coast - 0.5) continue;
        grid.set(gx, seaLevel + 2, gz, P.bollard);
    }

    // --- the dijk: tall narrow houses looking down on the harbour ---------
    const lights = [];
    let placed = 0;
    for (let i = 0; i < 26 && placed < 11; i++) {
        const a = mouth + Math.PI + (i / 25 - 0.5) * 4.4;
        const r = basin.radius + 3.5 + (i % 2) * 2.2;
        const gx = Math.round(basin.cx + Math.sin(a) * r);
        const gz = Math.round(basin.cz + Math.cos(a) * r);
        if (!polder.isDryLand(gx, gz)) continue;

        // Narrow and tall, gable to the water, which is how they stand there.
        const ok = stampHouse(grid, props, polder, gx, gz, {
            w: 4 + Math.floor(rng() * 2),
            d: 5,
            wallHeight: 5 + (rng() < 0.45 ? 1 : 0),
            wall: rng() < 0.55 ? P.wall : P.wallPale,
            roof: P.roof,
            rng,
        });
        if (ok) {
            placed++;
            lights.push({ x: gx, y: polder.top(gx, gz) + 4, z: gz, range: 10 });
        }
    }

    // --- the Sint-Vincentiuskerk ------------------------------------------
    // Set back behind the dijk, tower to the harbour: masts first, then a
    // spire above the roofs. It is the tallest thing on the island.
    // Searched out from the middle of the island rather than out from the
    // basin: measured from the harbour, anything far enough back to be behind
    // the houses is already past the far coast.
    let spire = null;
    const mid = size / 2;
    for (let i = 0; i < 60 && !spire; i++) {
        const a = mouth + (rng() - 0.5) * 2.0;      // the harbour side, roughly
        const r = size * (0.06 + rng() * 0.16);
        const gx = Math.round(mid + Math.sin(a) * r);
        const gz = Math.round(mid + Math.cos(a) * r);

        // It needs a whole footprint of dry ground, not just a dry middle —
        // and to be clear of the water it looks out over.
        let room = Math.hypot(gx - basin.cx, gz - basin.cz) > basin.radius + 5;
        for (let dx = -13; dx <= 13 && room; dx += 2) {
            for (let dz = -8; dz <= 8 && room; dz += 2) {
                if (!polder.isDryLand(gx + dx, gz + dz)) room = false;
            }
        }
        if (!room) continue;

        const built = createChurch(palette, material, church, { voxelSize });
        const at = polder.toWorld(gx, gz);
        built.mesh.position.set(at.x, polder.top(gx, gz) * voxelSize, at.z);
        // Tower towards the water, nave running inland behind it.
        built.mesh.rotation.y = Math.atan2(Math.sin(mouth), Math.cos(mouth)) + Math.PI / 2;
        scene.add(built.mesh);

        // Something to point at: a column around the tower, since the spire
        // itself is a few voxels wide at any distance worth reading it from.
        const pick = new THREE.Mesh(
            new THREE.CylinderGeometry(5 * voxelSize, 7 * voxelSize, built.height, 10),
            PICK_MATERIAL,
        );
        pick.position.set(at.x, polder.top(gx, gz) * voxelSize + built.height / 2, at.z);
        pick.userData.landmark = LANDMARKS.volendam;
        scene.add(pick);

        spire = {
            x: gx,
            z: gz,
            mesh: built.mesh,
            height: built.height,
            pick,
            // Where the card hangs: about halfway up, not off the spire. A
            // card pinned to the top of the tallest thing on the island ends
            // up above the window.
            position: new THREE.Vector3(
                at.x,
                polder.top(gx, gz) * voxelSize + built.height * 0.45,
                at.z,
            ),
        };
    }

    return { basin, lights, mouth, spire };
}
